/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * 初始化指定空间的当前用户角色，供 useSpaceAuth / useSpaceRole 进行权限计算。
 */

import { useEffect } from 'react';

import { useShallow } from 'zustand/react/shallow';
import { SpaceRoleType } from '@coze-arch/idl/developer_api';
import { useSpaceAuthStore } from '@coze-common/auth';
import { PlaygroundApi } from '@coze-arch/bot-api';

export function useInitSpaceRole(spaceId: string) {
  const { setIsReady, setRoles, isReady } = useSpaceAuthStore(
    useShallow(store => ({
      setIsReady: store.setIsReady,
      setRoles: store.setRoles,
      isReady: store.isReady[spaceId],
    })),
  );

  useEffect(() => {
    let cancelled = false;

    (async () => {
      // 拉取失败时降级为普通成员，避免授予过高权限
      let role = SpaceRoleType.Member;
      try {
        const res = await PlaygroundApi.SpaceMemberDetailV2({
          space_id: spaceId,
        });
        if (res.code === 0 && res.data?.space_role_type !== undefined) {
          role = res.data.space_role_type as unknown as SpaceRoleType;
        }
      } catch {
        // 拉取失败时保持默认成员角色，避免授予过高权限
        role = SpaceRoleType.Member;
      }

      if (!cancelled) {
        setRoles(spaceId, [role]);
        setIsReady(spaceId, true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [spaceId]);

  return isReady;
}
